import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { finalize } from 'rxjs';
import {
  readVaultItemsPerPageFromPreferences,
  VaultAppToastService,
  VAULT_TABLE_PAGE_SIZE_OPTIONS,
  VAULT_TABLE_PAGINATION_LABELS,
} from '@nebula/shared-vault/data-access';
import {
  patchVaultModuleInitialLoadError,
  toModuleAccessErrorMessage,
  type PageHeaderAction,
} from '@nebula-shared/ui';
import { type ActionEvent, type TableConfig } from 'nebula-ui-kit';
import { LegalHoldService } from '../../../data-access/src/lib/legal-hold/legal-hold.service';
import {
  type LegalHold,
  type LegalHoldDocument,
  type LegalHoldDocumentType,
  type LegalHoldStatus,
  type LegalHoldWithDocuments,
} from '../../../domain/src/lib/legal-hold/legal-hold.model';
import {
  formatLegalHoldDateDisplay,
  matchesLegalHoldSearch,
  parseLegalHoldDateForPicker,
  toLegalHoldDateApi,
  toLegalHoldStartDateApi,
  toLegalHoldStatusLabel,
} from '../../../feature-vault/src/lib/legal/legal-hold.utils';
import {
  buildLegalHoldCaseCreatedToast,
  buildLegalHoldCaseUpdatedToast,
  buildVaultSuccessToast,
} from '../../../feature-vault/src/lib/vault-module-feedback.util';
import { loadPersistedState, savePersistedState } from './signal-store-persist.util';

export interface LegalHoldRow extends LegalHold {
  statusLabel: string;
  startDateLabel: string;
}

export interface DocumentOption {
  id: string;
  name: string;
  type: LegalHoldDocumentType;
  inHold: boolean;
}

export interface CaseForm {
  case_name: string;
  case_number: string;
  description: string;
  end_date: Date | null;
}

interface LegalHoldStoreState {
  loading: boolean;
  saving: boolean;
  accessDenied: boolean;
  loadError: string | null;
  loadErrorTitle: string | null;
  error: string | null;
  info: string | null;
  rows: LegalHoldRow[];
  selected: LegalHoldWithDocuments | null;
  statusFilter: 'all' | 'active' | 'closed';
  searchTerm: string;
  assignmentDialogOpen: boolean;
  assignmentSearch: string;
  assignmentType: LegalHoldDocumentType;
  assignmentReason: string;
  selectedDocumentId: string;
  availableDocuments: DocumentOption[];
  creating: boolean;
  editingId: string | null;
  form: CaseForm;
  formNameError: string | null;
}

type LegalHoldPersistedState = Pick<
  LegalHoldStoreState,
  'statusFilter' | 'searchTerm' | 'assignmentType' | 'creating' | 'editingId' | 'form'
>;

const STORAGE_KEY = 'vault_legal_hold_state';
const STORAGE_VERSION = 1;
const persistConfig = {
  key: STORAGE_KEY,
  version: STORAGE_VERSION,
  storage: 'session' as const,
};

const statusOptions: Array<{ label: string; value: 'all' | 'active' | 'closed' }> = [
  { label: 'Todos', value: 'all' },
  { label: 'Activos', value: 'active' },
  { label: 'Cerrados', value: 'closed' },
] ;

const assignmentTypeOptions: Array<{ label: string; value: LegalHoldDocumentType }> = [
  { label: 'Ingesta', value: 'ingest' },
  { label: 'RRHH', value: 'hr_document' },
];

const tableConfig: TableConfig<LegalHoldRow> = {
  columns: [
    { key: 'case_name', label: 'Caso', align: 'left' },
    { key: 'case_number', label: 'No. caso', align: 'left', width: '140px' },
    { key: 'statusLabel', label: 'Estado', align: 'center', width: '120px', cellTemplate: true },
    { key: 'documents_count', label: 'Documentos', align: 'center', width: '110px' },
    { key: 'startDateLabel', label: 'Inicio', align: 'center', width: '140px' },
    { key: '_actions', label: 'Acciones', align: 'center', width: '10.5rem', cellTemplate: true },
  ],
  showPagination: true,
  rowClickable: true,
  defaultAction: 'view',
  pageSize: readVaultItemsPerPageFromPreferences(),
  pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
  paginationLabels: { ...VAULT_TABLE_PAGINATION_LABELS },
  ariaLabel: 'Casos de custodia legal',
};

const initialState: LegalHoldStoreState = {
  loading: false,
  saving: false,
  accessDenied: false,
  loadError: null,
  loadErrorTitle: null,
  error: null,
  info: null,
  rows: [],
  selected: null,
  statusFilter: 'all',
  searchTerm: '',
  assignmentDialogOpen: false,
  assignmentSearch: '',
  assignmentType: 'ingest',
  assignmentReason: '',
  selectedDocumentId: '',
  availableDocuments: [],
  creating: false,
  editingId: null,
  form: emptyForm(),
  formNameError: null,
};

function isLegalHoldPersistedState(value: unknown): value is LegalHoldPersistedState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  const statusOk = v['statusFilter'] === 'all' || v['statusFilter'] === 'active' || v['statusFilter'] === 'closed';
  const assignmentTypeOk = v['assignmentType'] === 'ingest' || v['assignmentType'] === 'hr_document';
  const editingIdOk = typeof v['editingId'] === 'string' || v['editingId'] === null;
  const creatingOk = typeof v['creating'] === 'boolean';
  const form = v['form'];
  const formOk =
    typeof form === 'object' &&
    form !== null &&
    typeof (form as Record<string, unknown>)['case_name'] === 'string' &&
    typeof (form as Record<string, unknown>)['case_number'] === 'string' &&
    typeof (form as Record<string, unknown>)['description'] === 'string' &&
    ((form as Record<string, unknown>)['end_date'] === null ||
      typeof (form as Record<string, unknown>)['end_date'] === 'string');
  return statusOk && assignmentTypeOk && creatingOk && editingIdOk && formOk && typeof v['searchTerm'] === 'string';
}

function normalizePersistedForm(form: CaseForm): CaseForm {
  return {
    ...form,
    end_date: parseLegalHoldDateForPicker(form.end_date as Date | string | null),
  };
}

const loadedPersisted = loadPersistedState(persistConfig, isLegalHoldPersistedState);
const persistedInitialState: LegalHoldStoreState = {
  ...initialState,
  ...(loadedPersisted ?? {}),
  ...(loadedPersisted?.form ? { form: normalizePersistedForm(loadedPersisted.form) } : {}),
};

function persistState(store: {
  statusFilter: () => 'all' | 'active' | 'closed';
  searchTerm: () => string;
  assignmentType: () => LegalHoldDocumentType;
  creating: () => boolean;
  editingId: () => string | null;
  form: () => CaseForm;
}): void {
  savePersistedState(persistConfig, {
    statusFilter: store.statusFilter(),
    searchTerm: store.searchTerm(),
    assignmentType: store.assignmentType(),
    creating: store.creating(),
    editingId: store.editingId(),
    form: store.form(),
  } satisfies LegalHoldPersistedState);
}

export const legalHoldStore = signalStore(
  { providedIn: 'root' },
  withState(persistedInitialState),
  withComputed((state) => ({
    statusOptions: computed(() => statusOptions),
    assignmentTypeOptions: computed(() => assignmentTypeOptions),
    tableConfig: computed(() => tableConfig),
    headerActions: computed<PageHeaderAction[]>(() => [
      { id: 'new', label: 'Nuevo caso', icon: 'Plus', variant: 'primary' },
      {
        id: 'reload',
        label: 'Actualizar',
        icon: 'RefreshCcw',
        variant: 'secondary',
        loading: state.loading(),
        disabled: state.loading(),
      },
    ]),
    filteredRows: computed(() =>
      state.rows().filter(
        (row) =>
          (state.statusFilter() === 'all' || row.status === state.statusFilter()) &&
          matchesLegalHoldSearch(row, state.searchTerm()),
      ),
    ),
    selectedDocuments: computed<LegalHoldDocument[]>(() => state.selected()?.documents || []),
  })),
  withMethods((store, legalHoldService = inject(LegalHoldService), vaultAppToast = inject(VaultAppToastService)) => ({
    init(): void {
      this.loadCases();
    },
    clearPageError(): void {
      patchState(store, { error: null });
    },
    clearPageInfo(): void {
      patchState(store, { info: null });
    },
    onHeaderAction(actionId: string): void {
      if (actionId === 'reload') this.loadCases();
      if (actionId === 'new') {
        patchState(store, { creating: true, editingId: null, form: emptyForm(), formNameError: null });
        persistState(store);
      }
    },
    onTableAction(ev: ActionEvent<LegalHoldRow>): void {
      const row = ev.data;
      if (!row) return;
      if (ev.action === 'view') this.openDetail(row.id);
      if (ev.action === 'edit') this.startEdit(row);
      if (ev.action === 'reopen') this.changeCaseStatus(row.id, false);
    },
    executePendingAction(pending: { type: 'delete' | 'close'; id: string; case_name: string }): void {
      patchState(store, { saving: true, error: null, info: null });

      if (pending.type === 'close') {
        legalHoldService
          .close(pending.id)
          .pipe(finalize(() => patchState(store, { saving: false })))
          .subscribe({
            next: () => {
              vaultAppToast.show(
                buildVaultSuccessToast('Caso cerrado', 'El caso de custodia legal se cerró correctamente.'),
              );
              patchState(store, { info: null });
              this.loadCases();
              if (store.selected()?.id === pending.id) this.openDetail(pending.id);
            },
            error: (err: unknown) =>
              patchState(store, {
                error: toModuleAccessErrorMessage(err, {
                  moduleLabel: 'Custodia Legal',
                  fallback: 'No se pudo cerrar el caso.',
                }),
              }),
          });
        return;
      }

      legalHoldService
        .delete(pending.id)
        .pipe(finalize(() => patchState(store, { saving: false })))
        .subscribe({
          next: () => {
            const wasSelected = store.selected()?.id === pending.id;
            const wasEditing = store.editingId() === pending.id;
            vaultAppToast.show(
              buildVaultSuccessToast('Caso eliminado', 'El caso se eliminó correctamente.'),
            );
            patchState(store, {
              info: null,
              selected: wasSelected ? null : store.selected(),
              editingId: wasEditing ? null : store.editingId(),
              creating: wasEditing ? false : store.creating(),
            });
            if (wasEditing) {
              patchState(store, { form: emptyForm() });
              persistState(store);
            }
            this.loadCases();
          },
          error: (err: unknown) =>
            patchState(store, {
              error: toModuleAccessErrorMessage(err, {
                moduleLabel: 'Custodia Legal',
                fallback: 'No se pudo eliminar el caso.',
              }),
            }),
        });
    },
    onFormField<K extends keyof CaseForm>(key: K, value: CaseForm[K]): void {
      patchState(store, {
        form: { ...store.form(), [key]: value },
        ...(key === 'case_name' ? { formNameError: null } : {}),
      });
      persistState(store);
    },
    onSearchTermChange(value: string): void {
      patchState(store, { searchTerm: value ?? '' });
      persistState(store);
    },
    onStatusFilterChange(value: string): void {
      patchState(store, { statusFilter: value === 'active' || value === 'closed' ? value : 'all' });
      persistState(store);
      this.loadCases();
    },
    onAssignmentTypeChange(value: string): void {
      patchState(store, { assignmentType: value === 'hr_document' ? 'hr_document' : 'ingest' });
      persistState(store);
      this.loadAvailableDocuments();
    },
    selectDocumentOption(id: string): void {
      patchState(store, { selectedDocumentId: id });
    },
    onAssignmentSearchChange(value: string): void {
      patchState(store, { assignmentSearch: value ?? '' });
      this.loadAvailableDocuments();
    },
    onAssignmentReasonChange(value: string): void {
      patchState(store, { assignmentReason: value ?? '' });
    },
    closeCaseForm(): void {
      patchState(store, { creating: false, editingId: null, form: emptyForm(), formNameError: null });
      persistState(store);
    },
    saveCase(): void {
      const payload = store.form();
      if (!payload.case_name.trim()) {
        patchState(store, { formNameError: 'El nombre del caso es obligatorio.' });
        return;
      }
      patchState(store, { saving: true, error: null, info: null, formNameError: null });
      const caseName = payload.case_name.trim();
      const isEdit = Boolean(store.editingId());
      const endDateApi = toLegalHoldDateApi(payload.end_date);
      const request$ = isEdit
        ? legalHoldService.update(store.editingId()!, {
            case_name: caseName,
            case_number: payload.case_number.trim() || undefined,
            description: payload.description.trim() || undefined,
            end_date: endDateApi,
          })
        : legalHoldService.create({
            case_name: caseName,
            case_number: payload.case_number.trim() || undefined,
            description: payload.description.trim() || undefined,
            start_date: toLegalHoldStartDateApi(),
            end_date: endDateApi,
            status: 'active',
          });

      request$.pipe(finalize(() => patchState(store, { saving: false }))).subscribe({
        next: () => {
          vaultAppToast.show(
            isEdit
              ? buildLegalHoldCaseUpdatedToast(caseName)
              : buildLegalHoldCaseCreatedToast(caseName),
          );
          patchState(store, {
            creating: false,
            editingId: null,
            form: emptyForm(),
            info: null,
          });
          persistState(store);
          this.loadCases();
        },
        error: (err: { error?: { detail?: string }; message?: string }) =>
          patchState(store, {
            error: toModuleAccessErrorMessage(err, {
              moduleLabel: 'Custodia Legal',
              fallback: 'No se pudo guardar el caso.',
            }),
          }),
      });
    },
    openAssignmentDialog(): void {
      if (!store.selected()) return;
      patchState(store, {
        assignmentDialogOpen: true,
        assignmentSearch: '',
        assignmentReason: '',
        selectedDocumentId: '',
      });
      this.loadAvailableDocuments();
    },
    closeAssignmentDialog(): void {
      patchState(store, { assignmentDialogOpen: false });
    },
    loadAvailableDocuments(): void {
      legalHoldService.getAvailableDocuments(store.assignmentSearch(), store.assignmentType(), 30, 0).subscribe({
        next: (res) => {
          const hr = (res.hr_documents || []).map((d) => ({
            id: d.id,
            name: d.name,
            type: 'hr_document' as const,
            inHold: !!d.is_in_legal_hold,
          }));
          const ingest = (res.ingest_documents || []).map((d) => ({
            id: d.id,
            name: d.name,
            type: 'ingest' as const,
            inHold: !!d.is_in_legal_hold,
          }));
          patchState(store, { availableDocuments: store.assignmentType() === 'hr_document' ? hr : ingest });
        },
        error: () => patchState(store, { availableDocuments: [] }),
      });
    },
    assignDocument(): void {
      const current = store.selected();
      const documentId = store.selectedDocumentId();
      if (!current || !documentId) return;

      patchState(store, { saving: true });
      legalHoldService
        .addDocument(current.id, {
          document_id: documentId,
          document_type: store.assignmentType(),
          reason: store.assignmentReason().trim() || undefined,
        })
        .pipe(finalize(() => patchState(store, { saving: false })))
        .subscribe({
          next: () => {
            vaultAppToast.show(
              buildVaultSuccessToast(
                'Documento asignado',
                'El documento se asignó al caso correctamente.',
              ),
            );
            patchState(store, { info: null, assignmentDialogOpen: false });
            this.openDetail(current.id);
            this.loadCases();
          },
          error: (err: { error?: { detail?: string }; message?: string }) =>
            patchState(store, {
              error: toModuleAccessErrorMessage(err, {
                moduleLabel: 'Custodia Legal',
                fallback: 'No se pudo asignar el documento.',
              }),
            }),
        });
    },
    removeDocument(doc: LegalHoldDocument): void {
      const current = store.selected();
      if (!current) return;
      patchState(store, { saving: true });
      legalHoldService
        .removeDocument(current.id, doc.document_id, doc.document_type)
        .pipe(finalize(() => patchState(store, { saving: false })))
        .subscribe({
          next: () => {
            vaultAppToast.show(
              buildVaultSuccessToast(
                'Documento removido',
                'El documento se removió del caso correctamente.',
              ),
            );
            patchState(store, { info: null });
            this.openDetail(current.id);
            this.loadCases();
          },
          error: (err: { error?: { detail?: string }; message?: string }) =>
            patchState(store, {
              error: toModuleAccessErrorMessage(err, {
                moduleLabel: 'Custodia Legal',
                fallback: 'No se pudo remover el documento.',
              }),
            }),
        });
    },
    openDetail(id: string): void {
      legalHoldService.get(id).subscribe({
        next: (detail) => patchState(store, { selected: detail }),
        error: (err: { error?: { detail?: string }; message?: string }) =>
          patchState(store, {
            error: toModuleAccessErrorMessage(err, {
              moduleLabel: 'Custodia Legal',
              fallback: 'No se pudo cargar el detalle.',
            }),
          }),
      });
    },
    startEdit(row: LegalHoldRow): void {
      patchState(store, {
        creating: true,
        editingId: row.id,
        formNameError: null,
        form: {
          case_name: row.case_name || '',
          case_number: row.case_number || '',
          description: row.description || '',
          end_date: parseLegalHoldDateForPicker(row.end_date),
        },
      });
      persistState(store);
    },
    changeCaseStatus(id: string, close: boolean): void {
      patchState(store, { saving: true });
      const request$ = close ? legalHoldService.close(id) : legalHoldService.reopen(id);
      request$.pipe(finalize(() => patchState(store, { saving: false }))).subscribe({
        next: () => {
          vaultAppToast.show(
            buildVaultSuccessToast(
              close ? 'Caso cerrado' : 'Caso reabierto',
              close
                ? 'El caso de custodia legal se cerró correctamente.'
                : 'El caso de custodia legal se reabrió correctamente.',
            ),
          );
          patchState(store, { info: null });
          this.loadCases();
          if (store.selected()?.id === id) this.openDetail(id);
        },
        error: (err: { error?: { detail?: string }; message?: string }) =>
          patchState(store, {
            error: toModuleAccessErrorMessage(err, {
              moduleLabel: 'Custodia Legal',
              fallback: 'No se pudo actualizar el estado.',
            }),
          }),
      });
    },
    loadCases(): void {
      patchState(store, {
        loading: true,
        error: null,
        loadError: null,
        loadErrorTitle: null,
        accessDenied: false,
      });
      const filterValue = store.statusFilter();
      const status: LegalHoldStatus | undefined = filterValue === 'active' || filterValue === 'closed' ? filterValue : undefined;
      legalHoldService
        .list(status)
        .pipe(finalize(() => patchState(store, { loading: false })))
        .subscribe({
          next: (rows) =>
            patchState(store, {
              rows: (rows || []).map((row) => ({
                ...row,
                statusLabel: toLegalHoldStatusLabel(row.status),
                startDateLabel: formatLegalHoldDateDisplay(row.start_date),
              })),
            }),
          error: (err: unknown) =>
            patchState(store, {
              ...patchVaultModuleInitialLoadError(err, {
                moduleLabel: 'Custodia Legal',
                fallback: 'No se pudieron cargar los casos de legal hold.',
              }),
              rows: [],
            }),
        });
    },
  })),
);

function emptyForm(): CaseForm {
  return {
    case_name: '',
    case_number: '',
    description: '',
    end_date: null,
  };
}

export type LegalHoldStore = typeof legalHoldStore;
